/**
 * How long a scale run's lines are and how they stand in the play area. Pure: the scene hands in
 * its measurements (OSMD px for widths, device px for heights) and draws with the answers.
 */

/**
 * The width (OSMD px) every line of a run is laid out in: a lesson phrase's width at the run's
 * size, as Sight Reading's lines are, and never narrower than the run's longest line at its
 * natural width (it would break onto a second line). Every line gets the same width, so the
 * lines of a run start and end together.
 */
export const runLineLayoutWidths = (lines: {
  /** Each line's width at its natural spacing, in OSMD px. */
  naturalWidthsPx: number[];
  /** A lesson phrase's width at the run's size, in OSMD px. */
  lessonWidthPx: number;
}): number[] => {
  const { naturalWidthsPx, lessonWidthPx } = lines;
  if (naturalWidthsPx.length === 0) return [];
  if (![lessonWidthPx, ...naturalWidthsPx].every((value) => value > 0)) {
    throw new Error('runLineLayoutWidths: every width must be positive');
  }
  const width = Math.round(Math.max(lessonWidthPx, ...naturalWidthsPx));
  return naturalWidthsPx.map(() => width);
};

/**
 * One line of a run as drawn (device px): its box, and where its music's ink (notes, ledger
 * lines, beams) begins and ends inside that box, measured from the box's top.
 */
export interface RunLineExtent {
  heightPx: number;
  inkTopPx: number;
  inkBottomPx: number;
}

/** How a run's lines are spaced down the play area (device px). */
export interface RunVerticalSpacing {
  /** From the play area's top to the first line's box. */
  topPaddingPx: number;
  /** From one line's box to the next line's box (negative when the boxes overlap). */
  lineGapPx: number;
}

/**
 * The lines stand under the tempo marking's room, each with a row of finger numbers above and
 * below its ink, and `systemGapPx` of clear space between one line's lowest finger numbers and
 * the next line's highest. The lines in view are kept together and the space they leave is
 * shared above and below them, never between them.
 */
export const runVerticalSpacing = (area: {
  heightPx: number;
  lines: RunLineExtent[];
  /** How many lines are in view at once (the rest scroll in). */
  linesInView: number;
  labelRowPx: number;
  topRoomPx: number;
  bottomMarginPx: number;
  systemGapPx: number;
}): RunVerticalSpacing => {
  const { heightPx, lines, linesInView, labelRowPx, topRoomPx, bottomMarginPx, systemGapPx } = area;
  if (lines.length === 0) {
    return { topPaddingPx: topRoomPx + labelRowPx, lineGapPx: systemGapPx + 2 * labelRowPx };
  }
  // Each line's content with its finger numbers, from its box's top.
  const contentTop = (line: RunLineExtent): number => line.inkTopPx - labelRowPx;
  const contentBottom = (line: RunLineExtent): number => line.inkBottomPx + labelRowPx;
  // A box runs on below its music, so the gap between boxes can be negative: the boxes overlap
  // and the music stands `systemGapPx` apart.
  const gapsBetweenBoxes = lines
    .slice(1)
    .map(
      (next, index) =>
        systemGapPx + contentBottom(lines[index]) - lines[index].heightPx - contentTop(next),
    );
  const lineGapPx = gapsBetweenBoxes.length > 0 ? Math.max(...gapsBetweenBoxes) : 0;
  const inView = lines.slice(0, Math.max(1, linesInView));
  const last = inView[inView.length - 1];
  const boxesHeight = inView.reduce((sum, line) => sum + line.heightPx, 0);
  const blockHeight =
    boxesHeight +
    (inView.length - 1) * lineGapPx -
    contentTop(inView[0]) -
    (last.heightPx - contentBottom(last));
  const share = Math.max(0, heightPx - topRoomPx - bottomMarginPx - blockHeight) / 2;
  return { topPaddingPx: topRoomPx + share - contentTop(inView[0]), lineGapPx };
};
